import { Button, Menu, MenuItem } from '@mui/material';
import { athleteDisplayName, WorkoutLayerDomain } from '@strength/domain';
import { isValidUUID } from '@strength/utils';
import { getRouteApi } from '@tanstack/react-router';
import { MdAutoFixHigh, MdExpandMore } from '@teamworksdev/icons';
import { useState, type MouseEvent } from 'react';
import { trpc } from '../../../../trpc/react';
import { cn } from '../../../components/utils';
import { useFlags } from '../../../context/featureFlag';
import { useViewer } from '../../../context/viewer';
import { useToast } from '../../../hooks/useToast';

interface AutoFixButtonProps {
  workoutId: string;
  workoutName?: string;
  profileId?: string;
}

interface AutoFixResult {
  blockDraft: {
    layerBefore?: WorkoutLayerDomain.WorkoutLayer;
    edits: WorkoutLayerDomain.WorkoutLayerEdit[];
    fixesApplied: string[];
  } | null;
  athleteDrafts: Array<{
    profileId: string;
    athleteDraft: {
      layerBefore?: WorkoutLayerDomain.WorkoutLayer;
      edits: WorkoutLayerDomain.WorkoutLayerEdit[];
      fixesApplied: string[];
    } | null;
  }>;
  errors?: Array<{
    profileId: string;
    error: string;
  }>;
}

// Type guard for error objects with a message property
function isErrorWithMessage(obj: unknown): obj is { message: string } {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    'message' in obj &&
    typeof (obj as { message: unknown }).message === 'string'
  );
}

const routeApi = getRouteApi('/orgAuth/orgWithNav/$teamId/block/$blockId');
export const AutoFixButton = ({
  workoutId,
  workoutName,
  profileId,
}: AutoFixButtonProps) => {
  const { blockId } = routeApi.useParams();
  const { toast } = useToast();
  const { org } = useViewer();
  const { showAutoFixButton } = useFlags();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const blockMetadataQuery = trpc.block.blockMetadata.useQuery({ blockId });

  const blockName = blockMetadataQuery.data?.block.blockName;
  const trackId = blockMetadataQuery.data?.track.trackId;
  const trackName = blockMetadataQuery.data?.track.trackName;
  const teamId = blockMetadataQuery.data?.team.teamId;
  const teamName = blockMetadataQuery.data?.team.teamName;

  // Fetch athlete profile name if viewing an athlete workout
  const athleteProfileQuery = trpc.roster.athleteProfile.useQuery(
    { profileId: profileId ?? '' },
    { enabled: !!profileId },
  );

  const handleMenuClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const autoFixMutation = trpc.block.draftWorkout.autoFixLayer.useMutation({
    onSuccess: (result: AutoFixResult) => {
      const { blockDraft, athleteDrafts, errors } = result;

      // Check if any fixes were applied
      const hasBlockFixes = blockDraft && blockDraft.edits.length > 0;
      const hasAthleteFixes = athleteDrafts && athleteDrafts.length > 0;
      const hasAnyFixes = hasBlockFixes || hasAthleteFixes;
      const hasErrors = errors && errors.length > 0;

      if (hasAnyFixes) {
        // Combine fixes from all layers
        const allFixes: string[] = [];
        if (hasBlockFixes) {
          allFixes.push(
            ...blockDraft.fixesApplied.map((fix: string) => `[Block] ${fix}`),
          );
        }
        if (hasAthleteFixes) {
          athleteDrafts.forEach(
            (athleteDraft: {
              profileId: string;
              athleteDraft: {
                fixesApplied: string[];
              } | null;
            }) => {
              if (athleteDraft.athleteDraft) {
                allFixes.push(
                  ...athleteDraft.athleteDraft.fixesApplied.map(
                    (fix: string) =>
                      `[Athlete ${athleteDraft.profileId}] ${fix}`,
                  ),
                );
              }
            },
          );
        }

        // Prepare comprehensive log data for support team
        const logData = {
          timestamp: new Date().toISOString(),
          orgId: org.orgId,
          blockId,
          workoutId,
          ...(workoutName && { workoutName }),
          fixesApplied: allFixes,
          athleteCount: athleteDrafts?.length || 0,
          ...(hasErrors && { errors }),
          ...(hasBlockFixes && {
            blockLayer: {
              layerBefore: blockDraft?.layerBefore,
              edits: blockDraft.edits,
            },
          }),
          ...(hasAthleteFixes && {
            athleteLayers: athleteDrafts.map(
              (ad: {
                profileId: string;
                athleteDraft: {
                  layerBefore?: WorkoutLayerDomain.WorkoutLayer;
                  edits: WorkoutLayerDomain.WorkoutLayerEdit[];
                  fixesApplied: string[];
                } | null;
              }) => ({
                profileId: ad.profileId,
                layerBefore: ad.athleteDraft?.layerBefore,
                edits: ad.athleteDraft?.edits,
                fixesApplied: ad.athleteDraft?.fixesApplied,
              }),
            ),
          }),
        };

        // Create formatted text for clipboard
        let formattedLog = `=== WORKOUT LAYER AUTO-FIX ===
Timestamp: ${logData.timestamp}
Org ID: ${logData.orgId}
Block ID: ${logData.blockId}
Workout ID: ${logData.workoutId}
${workoutName ? `Workout Name: ${workoutName}\n` : ''}Athletes Fixed: ${
          logData.athleteCount
        }
Fixes Applied:
${allFixes.map((fix, i) => `  ${i + 1}. ${fix}`).join('\n')}
${
  hasErrors && errors
    ? `\n⚠️ ERRORS (${errors.length} profiles failed):\n${errors
        .map((err, i) => `  ${i + 1}. Profile ${err.profileId}: ${err.error}`)
        .join('\n')}\n`
    : ''
}`;

        if (hasBlockFixes) {
          formattedLog += `
Block Layer Before:
${JSON.stringify(blockDraft.layerBefore, null, 2)}

Block Layer Edits:
${JSON.stringify(blockDraft.edits, null, 2)}
`;
        }

        if (hasAthleteFixes) {
          athleteDrafts.forEach(
            (
              athleteDraft: {
                profileId: string;
                athleteDraft: {
                  layerBefore?: WorkoutLayerDomain.WorkoutLayer;
                  edits: WorkoutLayerDomain.WorkoutLayerEdit[];
                } | null;
              },
              idx: number,
            ) => {
              formattedLog += `
Athlete ${idx + 1} (${athleteDraft.profileId}) Layer Before:
${JSON.stringify(athleteDraft.athleteDraft?.layerBefore, null, 2)}

Athlete ${idx + 1} Layer Edits:
${JSON.stringify(athleteDraft.athleteDraft?.edits, null, 2)}
`;
            },
          );
        }

        // Show toast with copy button
        toast({
          title:
            hasErrors && errors
              ? `Fixes applied with ${errors.length} error(s)`
              : 'Fixes applied',
          description:
            hasErrors && errors
              ? `Some athlete layers failed to update. Copy details for manual fixes.`
              : undefined,
          severity: hasErrors ? 'warning' : 'success',
          duration: hasErrors ? 12000 : 8000, // Longer duration for errors
          action: (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(formattedLog);
              }}
              sx={{
                mt: 1,
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Copy Details
            </Button>
          ),
        });
      } else if (hasErrors && errors) {
        // Only errors, no successful fixes
        const formattedLog = `=== WORKOUT LAYER AUTO-FIX ===
Timestamp: ${new Date().toISOString()}
Org ID: ${org.orgId}
Block ID: ${blockId}
Workout ID: ${workoutId}
${workoutName ? `Workout Name: ${workoutName}\n` : ''}
⚠️ ALL FIXES FAILED (${errors.length} profiles):
${errors
  .map((err, i) => `  ${i + 1}. Profile ${err.profileId}: ${err.error}`)
  .join('\n')}
`;

        toast({
          title: `Auto-fix failed for all ${errors.length} profile(s)`,
          description: 'Copy details for manual investigation',
          severity: 'error',
          duration: 12000,
          action: (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                navigator.clipboard.writeText(formattedLog);
              }}
              sx={{
                mt: 1,
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Copy Details
            </Button>
          ),
        });
      } else {
        toast({
          title: 'No fixes needed',
          severity: 'info',
        });
      }
    },
    onError: (error: unknown) => {
      const errorMessage = isErrorWithMessage(error)
        ? error.message
        : String(error) || 'An error occurred';

      toast({
        title: 'Auto-fix failed',
        description: errorMessage,
        severity: 'error',
      });
    },
  });

  const handleCopyInfo = () => {
    const athleteProfile = athleteProfileQuery.data;
    const athleteName = athleteProfile
      ? athleteDisplayName(athleteProfile)
      : undefined;

    const info = `Workout: ${workoutName || 'N/A'}
Workout ID: ${workoutId}

Block Name: ${blockName}
Block ID: ${blockId}

Org: ${org.orgName}
Org ID: ${org.orgId}

Track Name: ${trackName}
Track ID: ${trackId || 'N/A'}

Team: ${teamName || 'N/A'}
Team ID: ${teamId || 'N/A'}
${
  profileId
    ? `\nProfile: ${athleteName || 'N/A'}\nProfile ID: ${profileId}`
    : ''
}`;

    navigator.clipboard.writeText(info);
    toast({
      title: 'Info copied to clipboard',
      severity: 'success',
    });
    handleMenuClose();
  };

  const handleFixLayer = async () => {
    handleMenuClose();

    if (!isValidUUID(blockId)) {
      toast({
        title: 'Auto-fix failed',
        description: 'Invalid block ID',
        severity: 'error',
      });
      return;
    }

    if (autoFixMutation.isLoading) return;

    autoFixMutation.mutate({
      blockId,
      workoutId,
    });
  };

  return (
    <>
      <button
        type="button"
        onClick={handleMenuClick}
        disabled={autoFixMutation.isLoading}
        className={cn(
          'inline-flex items-center justify-center gap-0.5',
          'hover:bg-blue-50 rounded p-1',
          'transition-colors duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
        )}
      >
        <MdAutoFixHigh className={cn('text-blue-600 h-4 w-4')} />
        <MdExpandMore className={cn('text-blue-600 h-3 w-3')} />
      </button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleCopyInfo}>Copy Info</MenuItem>
        {showAutoFixButton && (
          <MenuItem onClick={handleFixLayer}>Fix Layer</MenuItem>
        )}
      </Menu>
    </>
  );
};
